/**
 * editor2/constants.ts
 * ReservationEditor v2 の定数定義
 * ReservationEditorPanel.tsx から抽出
 */

// --- 自動変更メモ フィールド設定 (75+ fields) ---
export const AUTO_MEMO_FIELD_CONFIGS = [
  // ステータス
  { field: 'status', label: 'ステータス', category: 'other', formatByStatus: true },
  // 日時
  { field: 'start_date', label: 'レンタル開始', category: 'other', formatDate: true },
  { field: 'end_date', label: 'レンタル終了', category: 'other', formatDate: true },
  { field: 'request_coming_time', label: '来店希望時刻', category: 'other' },
  { field: 'request_returning_time', label: '返却希望時刻', category: 'other' },
  // 車両
  { field: 'car_type', label: '車種', category: 'other' },
  { field: 'vehicle_id', label: '車両ID', category: 'other' },
  // 顧客情報
  { field: 'customer_name', label: '顧客名', category: 'reservation' },
  { field: 'customer_email', label: 'メール', category: 'reservation' },
  { field: 'customer_tel', label: '電話番号', category: 'reservation' },
  { field: 'customer_address', label: '住所', category: 'reservation' },
  // 乗車人数
  { field: 'pax', label: '乗車人数', category: 'reservation' },
  { field: 'pax_children', label: '子供人数', category: 'reservation' },
  { field: 'child_seat', label: 'チャイルドシート', category: 'reservation' },
  { field: 'booster_seat', label: 'ブースターシート', category: 'reservation' },
  { field: 'junior_seat', label: 'ジュニアシート', category: 'reservation' },
  // フライト
  { field: 'arrival_flight', label: '到着便', category: 'reservation' },
  { field: 'departure_flight', label: '出発便', category: 'reservation' },
  // 媒体・プラン
  { field: 'contact_media', label: '予約媒体', category: 'reservation' },
  { field: 'contact_plan', label: 'プラン', category: 'reservation' },
  // 料金
  { field: 'base_price', label: '基本料金', category: 'payment', formatPrice: true },
  { field: 'option_price', label: 'オプション料金', category: 'payment', formatPrice: true },
  { field: 'noc_price', label: 'NOC料金', category: 'insurance', formatPrice: true },
  { field: 'cdw_price', label: 'CDW料金', category: 'insurance', formatPrice: true },
  { field: 'sum_price', label: '合計金額', category: 'payment', formatPrice: true },
  { field: 'coupon', label: 'クーポン', category: 'payment' },
  { field: 'point', label: 'ポイント', category: 'payment' },
  { field: 'days', label: '日数', category: 'other' },
  { field: 'nights', label: '泊数', category: 'other' },
  // NOC/CDW トグル
  { field: 'noc', label: 'NOC', category: 'insurance', formatBool: true },
  { field: 'cdw', label: 'CDW', category: 'insurance', formatBool: true },
  // 確認・同意
  { field: 'license', label: '免許証確認', category: 'other', formatCheck: true },
  { field: 'payment', label: '決済確認', category: 'other', formatCheck: true },
  { field: 'terms_and_conditions', label: '利用規約同意', category: 'other', formatCheck: true },
  { field: 'disclaimer', label: '免責同意', category: 'other', formatCheck: true },
  { field: 'agreement', label: '契約同意', category: 'other', formatCheck: true },
  // メール送信フラグ
  { field: 'mail_send_flg1', label: '予約確定メール', category: 'other', formatBool: true },
  { field: 'mail_send_flg2', label: '決済御礼メール', category: 'other', formatBool: true },
  { field: 'mail_send_flg3', label: '利用御礼メール', category: 'other', formatBool: true },
  // 連絡手段
  { field: 'communication_tools', label: '連絡手段', category: 'reservation', formatCommTools: true },
  // 色
  { field: 'bg_color', label: '背景色', category: 'other' },
  { field: 'text_color', label: '文字色', category: 'other' },
  // その他
  { field: 'short_memo', label: 'ショートメモ', category: 'other' },
  { field: 'option', label: 'オプション', category: 'other' },
  { field: 'comment', label: 'コメント', category: 'other' },
  { field: 'invoice_link', label: 'インボイス', category: 'payment' },
] as const;

export type AutoMemoFieldConfig = (typeof AUTO_MEMO_FIELD_CONFIGS)[number];

// --- アクションラベル ---
export const ACTION_LABELS: Record<string, string> = {
  reservation: '予約処理',
  cancel: 'キャンセル処理',
  payment: '決済処理',
  insurance: '補償処理',
  other: 'その他処理',
};

// --- カテゴリ優先度: 決済 > 補償 > 予約 > その他 ---
export const ACTION_PRIORITY = ['payment', 'insurance', 'reservation', 'other'] as const;

// --- カテゴリ → アクション マッピング ---
export const CATEGORY_TO_ACTION: Record<string, string> = {
  cancel: 'cancel',
  payment: 'payment',
  insurance: 'insurance',
  reservation: 'reservation',
  other: 'other',
};

// --- 連絡手段マスタ ---
export const COMMUNICATION_TOOLS_MASTER = [
  { id: 1, name: 'TEL' },
  { id: 2, name: 'LINE' },
  { id: 3, name: 'SMS' },
  { id: 4, name: 'WhatsApp' },
  { id: 5, name: 'Instagram' },
  { id: 6, name: 'その他' },
] as const;

// communication_tools ID → 名前マッピング
export const COMM_TOOL_NAMES: Record<number, string> = {
  1: 'TEL',
  2: 'LINE',
  3: 'SMS',
  4: 'WhatsApp',
  5: 'Instagram',
  6: 'その他',
};

// --- 予約媒体の候補 ---
export const CONTACT_MEDIA_OPTIONS = [
  'lp[ja]',
  'lp[en]',
  'lp[tw]',
  'lp[kr]',
  'JA',
  'レンナビ',
  'okiren',
  'エアトリ',
  'rcom',
  '紹介',
  'TEL',
  'リピート',
  'その他',
] as const;

// --- メールフッターテンプレート ---
export const MAIL_FOOTER_TEMPLATES: Record<string, string> = {
  ja: `--------------------
Nレンタカー 那覇空港店
N RENT A CAR NAHA AIRPORT

901-0145 沖縄県那覇市高良3丁目3-19
3-3-19 Takara, Naha-shi, Okinawa, Japan 901-0145

TEL (9:00-21:00): 098-995-8517
LINE（緊急対応）: https://lin.ee/pkQzUiG

Nレンタカー那覇空港店WEBサイト: https://n-car.jp
Operated by 株式会社とれらんちゅ Trailernchu Inc. https://trailernchu.co.jp
--------------------`,
  en: `--------------------
Nレンタカー 那覇空港店
N RENT A CAR NAHA AIRPORT

901-0145 沖縄県那覇市高良3丁目3-19
3-3-19 Takara, Naha-shi, Okinawa, Japan 901-0145

TEL (9:00-21:00): 098-995-8517
LINE (Emergency): https://lin.ee/pkQzUiG

N RENT A CAR Website: https://n-car.jp
Operated by 株式会社とれらんちゅ Trailernchu Inc. https://trailernchu.co.jp
--------------------`,
  tw: `--------------------
Nレンタカー 那覇空港店
N RENT A CAR NAHA AIRPORT

901-0145 沖縄県那覇市高良3丁目3-19
3-3-19 Takara, Naha-shi, Okinawa, Japan 901-0145

TEL (9:00-21:00): 098-995-8517
LINE（緊急應對）: https://lin.ee/pkQzUiG

N RENT A CAR 官方網站: https://n-car.jp
Operated by 株式会社とれらんちゅ Trailernchu Inc. https://trailernchu.co.jp
--------------------`,
  kr: `--------------------
Nレンタカー 那覇空港店
N RENT A CAR NAHA AIRPORT

901-0145 沖縄県那覇市高良3丁目3-19
3-3-19 Takara, Naha-shi, Okinawa, Japan 901-0145

TEL (9:00-21:00): 098-995-8517
LINE (긴급 대응): https://lin.ee/pkQzUiG

N RENT A CAR 웹사이트: https://n-car.jp
Operated by 株式会社とれらんちゅ Trailernchu Inc. https://trailernchu.co.jp
--------------------`,
};

// --- メールテンプレートタイプ ---
export const EMAIL_TEMPLATE_TYPES = [
  'payment',
  'free',
  'request_ok',
  'request_full',
  'request_offer',
  'cancel',
  'cancel_fee',
  'comment',
] as const;

export type EmailTemplateType = (typeof EMAIL_TEMPLATE_TYPES)[number];

// --- SAFE_DEFAULTS: editing が null の場合のフォールバック ---
export const SAFE_DEFAULTS = {
  id: '',
  contact_id: '',
  contact_code: '',
  page_id: '',
  customer_name: '',
  customer_email: '',
  customer_tel: '',
  customer_address: '',
  start_date: '',
  end_date: '',
  resourceId: '',
  resource_id: '',
  vehicle_id: '',
  vehicle_code: '',
  car_type: '',
  status: null as string | null,
  contact_media: '',
  arrival_flight: '',
  departure_flight: '',
  contact_plan: '',
  pax: 0,
  pax_children: 0,
  booster_seat: 0,
  child_seat: 0,
  junior_seat: 0,
  invoice_link: '',
  comment: '',
  memo: '',
  option: '',
  base_price: null as number | null,
  option_price: null as number | null,
  noc: false,
  noc_price: null as number | null,
  cdw: false,
  cdw_price: null as number | null,
  sum_price: null as number | null,
  days: null as number | null,
  nights: null as number | null,
  coupon: '',
  point: null as number | null,
  bg_color: '',
  text_color: '',
  booking_date: '',
  past_reservation_count: 0,
  mail_send_flg1: false,
  mail_send_flg2: false,
  mail_send_flg3: false,
  communication_tools: [] as number[],
  communication_tools_display: '',
  emergency_name: '',
  emergency_rel: '',
  emergency_tel: '',
  license: false,
  payment: false,
  terms_and_conditions: false,
  disclaimer: false,
  agreement: false,
  short_memo: '',
  request_coming_time: '',
  request_returning_time: '',
  rental_location: '',
  return_location: '',
  pu_staff_id: null as string | null,
  do_staff_id: null as string | null,
  rent_staff_id: null as string | null,
  return_staff_id: null as string | null,
  json_data: null as any,
  __isNew: false,
  __isCopy: false,
} as const;
